import { useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import type { HistoryEntry, HistoryStats, EntryCategory, ActorType } from '../../types/history'
import { EntryCard } from './EntryCard'
import { TimelineHeader } from './TimelineHeader'
import { TimelineFilters } from './TimelineFilters'
import { TimelinePagination } from './TimelinePagination'

const ENTRIES_PER_PAGE = 25

function groupEntriesByDate(entries: HistoryEntry[]): Map<string, HistoryEntry[]> {
  const groups = new Map<string, HistoryEntry[]>()
  for (const entry of entries) {
    const dateKey = new Date(entry.timestamp).toDateString()
    if (!groups.has(dateKey)) {
      groups.set(dateKey, [])
    }
    const group = groups.get(dateKey)
    if (group) group.push(entry)
  }
  return groups
}

function getDateLabel(dateStr: string, translate: { today: string; yesterday: string }): string {
  const date = new Date(dateStr)
  const now = new Date()
  const yesterday = new Date(now)
  yesterday.setDate(yesterday.getDate() - 1)
  if (date.toDateString() === now.toDateString()) return translate.today
  if (date.toDateString() === yesterday.toDateString()) return translate.yesterday
  return date.toLocaleDateString(undefined, { weekday: 'long', month: 'short', day: 'numeric' })
}

interface DateGroupProps {
  dateKey: string
  dateEntries: HistoryEntry[]
  expandedIds: Set<string>
  onToggleExpanded: (id: string) => void
  onViewArtifact?: ((artifactId: string) => void) | undefined
  onConvertToMemory?: ((entryId: string) => void) | undefined
  onViewRelated?: ((entryIds: string[]) => void) | undefined
}

function DateGroup({
  dateKey,
  dateEntries,
  expandedIds,
  onToggleExpanded,
  onViewArtifact,
  onConvertToMemory,
  onViewRelated,
}: DateGroupProps): React.ReactElement {
  const { t } = useTranslation('history')
  return (
    <div key={dateKey}>
      <div className="mb-4 flex items-center gap-3">
        <div className="h-px flex-1 bg-slate-200 dark:bg-slate-800" />
        <span className="px-2 text-sm font-medium text-slate-500 dark:text-slate-400">
          {dateEntries[0]
            ? getDateLabel(dateEntries[0].timestamp, {
                today: t('dateToday'),
                yesterday: t('dateYesterday'),
              })
            : ''}
        </span>
        <div className="h-px flex-1 bg-slate-200 dark:bg-slate-800" />
      </div>
      <div className="space-y-3">
        {dateEntries.map((entry) => (
          <EntryCard
            key={entry.id}
            entry={entry}
            expanded={expandedIds.has(entry.id)}
            onToggle={() => {
              onToggleExpanded(entry.id)
            }}
            onViewArtifact={onViewArtifact}
            onConvertToMemory={onConvertToMemory}
            onViewRelated={onViewRelated}
          />
        ))}
      </div>
    </div>
  )
}

interface HistoryTimelineProps {
  entries: HistoryEntry[]
  stats: HistoryStats
  caseName?: string
  onViewArtifact?: (artifactId: string) => void
  onConvertToMemory?: (entryId: string) => void
  onExport?: (format: string) => void
  onViewRelated?: (entryIds: string[]) => void
}

interface TimelineState {
  searchQuery: string
  selectedCategories: EntryCategory[]
  selectedActors: ActorType[]
  showSystemEvents: boolean
  expandedIds: Set<string>
  currentPage: number
  filteredEntries: HistoryEntry[]
  totalPages: number
  groupedEntries: Map<string, HistoryEntry[]>
  setSearchQuery: (val: string) => void
  setSelectedCategories: (cats: EntryCategory[]) => void
  setSelectedActors: (actors: ActorType[]) => void
  setShowSystemEvents: (show: boolean) => void
  setCurrentPage: (page: number) => void
  toggleExpanded: (id: string) => void
}

function useTimelineState(entries: HistoryEntry[]): TimelineState {
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedCategories, setSelectedCategories] = useState<EntryCategory[]>([])
  const [selectedActors, setSelectedActors] = useState<ActorType[]>([])
  const [showSystemEvents, setShowSystemEvents] = useState(false)
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set())
  const [currentPage, setCurrentPage] = useState(1)

  const toggleExpanded = (id: string): void => {
    setExpandedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }

  const filteredEntries = useMemo(() => {
    return entries.filter((entry) => {
      if (!showSystemEvents && entry.actorType === 'system') return false
      if (searchQuery && !entry.summary.toLowerCase().includes(searchQuery.toLowerCase()))
        return false
      if (selectedCategories.length > 0 && !selectedCategories.includes(entry.category))
        return false
      if (selectedActors.length > 0 && !selectedActors.includes(entry.actorType)) return false
      return true
    })
  }, [entries, searchQuery, selectedCategories, selectedActors, showSystemEvents])

  const totalPages = Math.ceil(filteredEntries.length / ENTRIES_PER_PAGE)
  const paginatedEntries = filteredEntries.slice(
    (currentPage - 1) * ENTRIES_PER_PAGE,
    currentPage * ENTRIES_PER_PAGE
  )
  const groupedEntries = groupEntriesByDate(paginatedEntries)

  return {
    searchQuery,
    selectedCategories,
    selectedActors,
    showSystemEvents,
    expandedIds,
    currentPage,
    filteredEntries,
    totalPages,
    groupedEntries,
    setSearchQuery,
    setSelectedCategories,
    setSelectedActors,
    setShowSystemEvents,
    setCurrentPage,
    toggleExpanded,
  }
}

export function HistoryTimeline(props: HistoryTimelineProps): React.ReactElement {
  const { entries, stats, onViewArtifact, onConvertToMemory, onExport, onViewRelated } = props
  const caseName = props.caseName ?? 'Investigation'
  const state = useTimelineState(entries)

  return (
    <div className="space-y-6">
      <TimelineHeader stats={stats} caseName={caseName} onExport={onExport} />
      <TimelineFilters
        searchQuery={state.searchQuery}
        onSearchChange={(value: string) => {
          state.setSearchQuery(value)
          state.setCurrentPage(1)
        }}
        selectedCategories={state.selectedCategories}
        onCategoriesChange={(cats: EntryCategory[]) => {
          state.setSelectedCategories(cats)
          state.setCurrentPage(1)
        }}
        selectedActors={state.selectedActors}
        onActorsChange={(actors: ActorType[]) => {
          state.setSelectedActors(actors)
          state.setCurrentPage(1)
        }}
        showSystemEvents={state.showSystemEvents}
        onShowSystemEventsChange={state.setShowSystemEvents}
      />
      <div className="space-y-6">
        {[...state.groupedEntries.entries()].map(([dateKey, dateEntries]) => (
          <DateGroup
            key={dateKey}
            dateKey={dateKey}
            dateEntries={dateEntries}
            expandedIds={state.expandedIds}
            onToggleExpanded={state.toggleExpanded}
            onViewArtifact={onViewArtifact}
            onConvertToMemory={onConvertToMemory}
            onViewRelated={onViewRelated}
          />
        ))}
      </div>
      {state.totalPages > 1 && (
        <TimelinePagination
          currentPage={state.currentPage}
          totalPages={state.totalPages}
          totalEntries={state.filteredEntries.length}
          entriesPerPage={ENTRIES_PER_PAGE}
          onPageChange={state.setCurrentPage}
        />
      )}
    </div>
  )
}

export default HistoryTimeline
